import { alias, custom, deserialize, serializable, SKIP } from 'serializr';
import { BaseModel } from './base-model';
import { Project } from './project.model';
import { UserStatus } from '../enums/user-status.enum';
import { EntityAccess } from './entity-access.model';
import { licensePoolType } from '@modules/settings/pages/users/constants';
import { UserProjectMappingRequest } from '@modules/settings/components/users-list-item/user-create-update-form';
import { immerable } from 'immer';
import { AccessLevel } from '@enums/access-level.enum';
import { PrivilegeTypes } from '@enums/privilege.enum';
import { UserType } from '@enums/user-type.enum';

export class User extends BaseModel {
  [immerable] = true;
  @serializable
  public email: string;
  @serializable
  public firstName: string;
  @serializable
  public lastName: string;
  @serializable
  public status: UserStatus;
  @serializable
  public isDeleted: boolean;
  @serializable
  public gdpr: boolean;
  @serializable
  public isAdmin = false;
  @serializable
  public isSuperAdmin = false;
  @serializable
  public createdById: number;
  @serializable
  public updatedById: number;
  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return v.map((entity) => new EntityAccess().deserialize(entity));
      },
    ),
  )
  public privileges: EntityAccess[];
  public createdBy: User;
  public updatedBy: User;
  public projects: Project[];
  @serializable
  public roleId: number;
  @serializable
  public roleType: string;
  @serializable
  public jobTitle: string;
  @serializable
  public authType: string;
  @serializable
  public identityUserAccountUUId: string;
  @serializable
  public licensePoolType: licensePoolType;
  @serializable
  public maxParallelRuns: number;
  @serializable
  public availableCommonPoolRuns: number;
  @serializable
  public isRecentlyUsed = false;
  @serializable
  public type: UserType;
  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public userProjectMappingRequests: UserProjectMappingRequest[];

  deserialize(input: any): this {
    return Object.assign(this, deserialize(User, input));
  }

  get name() {
    if (this.firstName == null && this.lastName == null) {
      return '-';
    }
    return `${this.firstName || ''} ${this.lastName || ''}`;
  }
  get userRole() {
    return this.isAdmin
      ? 'Account Admin'
      : this.isSuperAdmin
      ? 'Super Admin'
      : '';
  }

  get domain() {
    return (this.email || '').split('@')[
      (this.email || '').split('@').length - 1
    ];
  }

  get fullName() {
    return `${this.firstName || ''} ${this.lastName || ''}`;
  }

  accessLevel(privilegeType: PrivilegeTypes): AccessLevel {
    const privilege = this.privileges?.find(
      (EntityAccess) =>
        EntityAccess.entity?.name == privilegeType
    );
    return AccessLevel[privilege?.accessLevel] ?? undefined;
  };

  hasWriteAccess(privilegeType): boolean {
    return this.isAdmin || this.isSuperAdmin || [AccessLevel.WRITE, AccessLevel.FULL_ACCESS].includes(this.accessLevel(privilegeType));
  }

  get isAPIUser() {
    return this.type === UserType.API_USER;
  }
  get isGuestUser() {
    return this.type === UserType.GUEST_USER;
  }
}
